import { Box, useMediaQuery, useTheme } from '@ublend-npm/aulaui-core';
import React, { useEffect, useState } from 'react';
import AssignmentSidebar from './AssignmentSidebar/AssignmentSidebar';
import { createStyles } from './AssignmentResponsive.styles';
import NavigationBackHeader from '../../common/NavigationBackHeader/NavigationBackHeader';
import useFetchAssignments from './hooks/useFetchAssignments';
import { getSpaceBackRoute } from './utils/navigation';
import { getCurrentSpaceId } from '@core/selectors/space';
import { useSelector } from 'react-redux';
import { AccessTokenState, AulaState, Space, User } from '@core/types/state';
import { getAssignments } from '@core/assignments/selectors';
import pushRoute from '@app/utils/routing';
import AssignmentDetails from './AssignmentDetails/AssignmentDetails';
import { ResponsiveAssignment } from '@core/assignments/types';
import MaterialPreviewModal from './MaterialPreviewModal/MaterialPreviewModal';
import { getSocket } from '@core/actions/socket';

type Props = {
  accessTokens: AccessTokenState;
  params?: {
    assignmentId?: string;
  };
  fetchAccessToken: ({
    spaceId,
  }: {
    spaceId: string;
  }) => Promise<string | null>;
  fetchAssignments: (params: {
    user: User | null;
    accessToken: string;
    spaceId: string;
  }) => void;
  isFetching: boolean;
  isEducator?: boolean;
  baseDomain: string;
  needsToRefreshAccessTokenProp: () => boolean;
  selectedSpace?: Space;
  user: User | null;
  isStaff: boolean;
  parentAssignmentSubmissions: any[];
  hasFetchedSubmissions: boolean;
  fetchSubmissions: (params: {
    accessToken: string;
    assignmentId: string;
  }) => void;
};

const resolveAssignmentId = (assignment?: ResponsiveAssignment | null) =>
  assignment?.id || assignment?.objectId || assignment?.externalId || '';

const AssignmentsResponsive = ({
  accessTokens,
  params,
  fetchAccessToken,
  fetchAssignments,
  isFetching,
  isEducator = false,
  baseDomain,
  needsToRefreshAccessTokenProp,
  selectedSpace,
  user,
  isStaff = false,
  parentAssignmentSubmissions = [],
  hasFetchedSubmissions = false,
  fetchSubmissions,
}: Props) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const state = useSelector((state: AulaState) => state);
  const socketId = useSelector((state: any) => state.socket?.id);
  const spaceId = getCurrentSpaceId(state);
  const assignments = getAssignments(state);
  const responsiveAssignments = assignments as ResponsiveAssignment[];
  const routeAssignmentId = params?.assignmentId;
  // Get the selected assignment based on the route assignment id
  const selectedAssignment = (() => {
    const routeMatch = responsiveAssignments.find(
      (assignment) => resolveAssignmentId(assignment) === routeAssignmentId
    );

    if (routeMatch) {
      return routeMatch;
    }

    return responsiveAssignments[0] || null;
  })();
  // Check if the selected assignment is a group assignment
  const isGroupAssignment = !!(selectedAssignment as any)?.groupSet;
  // Check if the selected assignment is an extension
  const isExtension = !!(selectedAssignment as any)?.parentAssignmentId;
  const showEmptyCohortAlert =
    hasFetchedSubmissions &&
    !isGroupAssignment &&
    !isExtension &&
    parentAssignmentSubmissions.length === 0;
  const backRoute = getSpaceBackRoute(spaceId);
  const baseRoute = `/dashboard/${spaceId}/journey/assignments`;
  const restoreKey = selectedSpace?.objectId
    ? `assignments:lastVisited:${selectedSpace.objectId}`
    : null;
  const getRestoredAssignmentId = () => {
    if (!restoreKey || typeof sessionStorage === 'undefined') {
      return null;
    }

    return sessionStorage.getItem(restoreKey);
  };
  const [highlightedAssignmentId, setHighlightedAssignmentId] = useState<
    string | null
  >(getRestoredAssignmentId);
  const [previewState, setPreviewState] = useState<{
    open: boolean;
    materialId: string | null;
    materialTitle: string;
  }>({
    open: false,
    materialId: null,
    materialTitle: '',
  });
  const requestAssignmentsRefresh = useFetchAssignments({
    accessTokens,
    fetchAccessToken,
    fetchAssignments,
    isFetching,
    needsToRefreshAccessTokenProp,
    selectedSpace,
    user,
  });

  const selectedAssignmentId = resolveAssignmentId(selectedAssignment);
  const restoredAssignmentId = responsiveAssignments.some(
    (assignment) => resolveAssignmentId(assignment) === highlightedAssignmentId
  )
    ? highlightedAssignmentId
    : null;
  const activeHighlight =
    routeAssignmentId &&
    routeAssignmentId !== 'list' &&
    restoredAssignmentId &&
    restoredAssignmentId !== routeAssignmentId
      ? null
      : restoredAssignmentId;
  const listHighlightedAssignmentId = activeHighlight || selectedAssignmentId;

  const handleFetchAccessToken = async (spaceId: string) => {
    if (needsToRefreshAccessTokenProp()) {
      return fetchAccessToken({ spaceId });
    }

    return accessTokens[spaceId]?.token || fetchAccessToken({ spaceId });
  };

  const handleFetchAssignments = async () => {
    const selectedSpaceId = selectedSpace?.objectId;

    if (isFetching || !selectedSpaceId) {
      return;
    }

    const accessToken = await handleFetchAccessToken(selectedSpaceId);

    if (accessToken) {
      fetchAssignments({
        user,
        accessToken,
        spaceId: selectedSpaceId,
      });
    }
  };

  useEffect(() => {
    if (!selectedSpace?.objectId) return undefined;
    const socket = getSocket();
    if (!socket) return undefined;

    const events = [
      'Material:SectionAdded',
      'Material:SectionUpdated',
      'Material:SectionDeleted',
    ];
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refresh = (data: { space?: string }) => {
      if (data?.space !== selectedSpace.objectId) return;
      clearTimeout(timer);
      timer = setTimeout(requestAssignmentsRefresh, 250);
    };
    events.forEach((event) => socket.on(event, refresh));
    return () => {
      clearTimeout(timer);
      events.forEach((event) => socket.off(event, refresh));
    };
  }, [socketId, selectedSpace?.objectId, requestAssignmentsRefresh]);

  useEffect(() => {
    if (!routeAssignmentId || !selectedSpace?.objectId) return;
    handleFetchAccessToken(selectedSpace.objectId).then((accessToken) => {
      if (accessToken)
        fetchSubmissions({ accessToken, assignmentId: routeAssignmentId });
    });
  }, [routeAssignmentId, selectedSpace?.objectId]);

  useEffect(() => {
    if (!selectedSpace?.objectId || !restoreKey) {
      setHighlightedAssignmentId(null);
      return;
    }

    const lastVisitedAssignmentId = sessionStorage.getItem(restoreKey);

    if (!lastVisitedAssignmentId) {
      setHighlightedAssignmentId(null);
      return;
    }

    setHighlightedAssignmentId(lastVisitedAssignmentId);
    sessionStorage.removeItem(restoreKey);
  }, [restoreKey, selectedSpace?.objectId]);

  const openMaterialPreview = (
    materialId: string,
    materialTitle?: string,
    assignmentId?: string
  ) => {
    if (!selectedSpace?.objectId) return;
    if (assignmentId) {
      sessionStorage.setItem(
        `assignments:lastVisited:${selectedSpace.objectId}`,
        assignmentId
      );
      setHighlightedAssignmentId(assignmentId);
    }
    setPreviewState({
      open: true,
      materialId,
      materialTitle: materialTitle || '',
    });
  };

  const onMaterialListClick = (assignmentId: string) => {
    if (!selectedSpace?.objectId) {
      return;
    }
    const storageKey = `assignments:lastVisited:${selectedSpace.objectId}`;
    sessionStorage.setItem(storageKey, assignmentId);
    setHighlightedAssignmentId(assignmentId);
  };

  const closeMaterialPreview = () => {
    setPreviewState({
      open: false,
      materialId: null,
      materialTitle: '',
    });
  };

  const clearRestoredAssignment = () => {
    if (!selectedSpace?.objectId) {
      return;
    }

    sessionStorage.removeItem(
      `assignments:lastVisited:${selectedSpace.objectId}`
    );
    setHighlightedAssignmentId(null);
  };

  const handleBackFromAssignments = () => {
    clearRestoredAssignment();
    if (backRoute) {
      pushRoute(backRoute);
    }
  };

  const handleBackToAssignmentList = () => {
    setHighlightedAssignmentId(selectedAssignmentId || null);
    pushRoute(`${baseRoute}/list`);
  };

  const defaultAssignmentId = resolveAssignmentId(responsiveAssignments[0]);
  const preferredAssignmentId = restoredAssignmentId || defaultAssignmentId;
  const previousViewRef = React.useRef<{
    assignmentIds: string[];
    selectedAssignmentId: string | null;
  }>({
    assignmentIds: [],
    selectedAssignmentId: null,
  });

  useEffect(() => {
    const currentAssignmentIds = responsiveAssignments.map((assignment) =>
      resolveAssignmentId(assignment)
    );
    const hasRouteAssignment = responsiveAssignments.some(
      (assignment) => resolveAssignmentId(assignment) === routeAssignmentId
    );
    const isMobileList =
      !isMdUp && (!routeAssignmentId || routeAssignmentId === 'list');
    const isDesktopDefaultRoute =
      isMdUp && (!routeAssignmentId || routeAssignmentId === 'list');

    if (isDesktopDefaultRoute) {
      if (preferredAssignmentId) {
        const fromMySpaceNav = !routeAssignmentId ? '?from=myspacenav' : '';
        pushRoute(`${baseRoute}/${preferredAssignmentId}${fromMySpaceNav}`);
      }
      previousViewRef.current = {
        assignmentIds: currentAssignmentIds,
        selectedAssignmentId: preferredAssignmentId || null,
      };
      return;
    }

    if (!assignments.length) {
      previousViewRef.current = {
        assignmentIds: currentAssignmentIds,
        selectedAssignmentId: null,
      };
      return;
    }

    if (isMobileList) {
      const previousView = previousViewRef.current;
      const prevIds = previousView.assignmentIds;
      const deletedId = prevIds.find(
        (id) => !currentAssignmentIds.includes(id)
      );

      if (deletedId) {
        const deletedIndex = prevIds.indexOf(deletedId);
        const fallbackIndex = Math.min(deletedIndex, assignments.length - 1);
        const fallbackAssignment =
          responsiveAssignments[fallbackIndex] || responsiveAssignments[0];
        const fallbackId = resolveAssignmentId(fallbackAssignment);
        setHighlightedAssignmentId(fallbackId || null);
      }

      previousViewRef.current = {
        assignmentIds: currentAssignmentIds,
        selectedAssignmentId: null,
      };
      return;
    }

    if (hasRouteAssignment) {
      previousViewRef.current = {
        assignmentIds: currentAssignmentIds,
        selectedAssignmentId,
      };
      return;
    }

    const previousView = previousViewRef.current;
    const deletedIndex = previousView.selectedAssignmentId
      ? previousView.assignmentIds.indexOf(previousView.selectedAssignmentId)
      : -1;
    const fallbackIndex =
      deletedIndex >= 0 ? Math.min(deletedIndex, assignments.length - 1) : 0;
    const fallbackAssignment =
      responsiveAssignments[fallbackIndex] || responsiveAssignments[0];
    const fallbackId = resolveAssignmentId(fallbackAssignment);

    if (fallbackId && fallbackId !== routeAssignmentId) {
      pushRoute(`${baseRoute}/${fallbackId}`);
    }

    previousViewRef.current = {
      assignmentIds: currentAssignmentIds,
      selectedAssignmentId: fallbackId || null,
    };
  }, [
    assignments,
    baseRoute,
    defaultAssignmentId,
    isMdUp,
    preferredAssignmentId,
    routeAssignmentId,
    selectedAssignmentId,
  ]);

  // Determine the current user role based on the educator and staff flags
  const currentUserRole = isEducator
    ? 'educator'
    : isStaff
    ? 'staff'
    : 'student';

  const getContent = () => {
    if (isMdUp) {
      return (
        <Box sx={styles.containerWrapper}>
          <Box sx={styles.leftWrapper}>
            <AssignmentSidebar
              assignments={assignments as ResponsiveAssignment[]}
              backRoute={backRoute}
              onBackClick={handleBackFromAssignments}
              spaceId={spaceId}
              baseDomain={baseDomain}
              isEducator={isEducator}
              highlightedAssignmentId={listHighlightedAssignmentId}
              onMaterialClick={openMaterialPreview}
              isDesktop={isMdUp}
              fetchAccessToken={handleFetchAccessToken}
              fetchAssignments={handleFetchAssignments}
              onMaterialListClick={onMaterialListClick}
              isLoading={isFetching}
            />
          </Box>
          <Box sx={styles.assignmentDetailsWrapper}>
            <AssignmentDetails
              currentUserRole={currentUserRole}
              assignments={assignments as ResponsiveAssignment[]}
              showEmptyCohortAlert={showEmptyCohortAlert}
              selectedAssignment={selectedAssignment}
              onMaterialListClick={onMaterialListClick}
              isLoading={isFetching}
            />
          </Box>
        </Box>
      );
    } else {
      return (
        <Box sx={styles.pageWrapper}>
          <Box sx={styles.buttonWrapper}>
            <NavigationBackHeader onClick={handleBackToAssignmentList} />
          </Box>
          <Box sx={styles.pageContent}>
            <Box sx={styles.assignmentDetailsMobile}>
              <AssignmentDetails
                currentUserRole={currentUserRole}
                assignments={assignments as ResponsiveAssignment[]}
                showEmptyCohortAlert={showEmptyCohortAlert}
                selectedAssignment={selectedAssignment}
                onMaterialListClick={onMaterialListClick}
                isLoading={isFetching}
              />
            </Box>
          </Box>
        </Box>
      );
    }
  };

  return (
    <>
      {/* On mobile, show the full-screen sidebar list when the route is /list.
          On desktop, getContent() always renders the split-pane shell which
          already includes the sidebar — so never show the standalone list on md+. */}
      {!isMdUp && (!routeAssignmentId || routeAssignmentId === 'list') ? (
        <Box sx={{ height: '100%' }}>
          <AssignmentSidebar
            assignments={assignments as ResponsiveAssignment[]}
            backRoute={backRoute}
            spaceId={spaceId}
            spaceName={selectedSpace?.name}
            baseDomain={baseDomain}
            isEducator={isEducator}
            highlightedAssignmentId={listHighlightedAssignmentId}
            onMaterialClick={openMaterialPreview}
            isDesktop={isMdUp}
            fetchAccessToken={handleFetchAccessToken}
            fetchAssignments={handleFetchAssignments}
            onMaterialListClick={onMaterialListClick}
            isLoading={isFetching}
          />
        </Box>
      ) : (
        <Box sx={styles.assignments}>{getContent()}</Box>
      )}
      <MaterialPreviewModal
        open={previewState.open}
        onClose={closeMaterialPreview}
        onGoToPage={() => {
          if (previewState.materialId) {
            pushRoute(
              `/dashboard/${spaceId}/journey/materials/${previewState.materialId}?from=assignments`
            );
          }
        }}
        spaceId={selectedSpace?.objectId}
        materialId={previewState.materialId}
        previewTitle={previewState.materialTitle}
      />
    </>
  );
};

export default AssignmentsResponsive;
